
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Container,
  Divider,
  Paper,
  Button,
  Stack,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import Footer from "./footer";

const sections = [
  {
    id: "Data Collection and Use Consent Form",
    title: "Data Collection and Use Consent Form",
    content: `
Data Collection and Use Consent Form
(Draft Version)
Last Updated: May 3, 2026


This consent form is intended for learners, parents or guardians, mentors, facilitators, and partner institutions using giiconnect. It explains what personal data may be collected, why it is collected, how it may be used, and the choices available to users. This draft should be reviewed by qualified legal counsel before publication or formal adoption.

1. Purpose of this Consent Form
giiconnect collects and uses limited personal data to provide digital learning, AI-supported educational assistance, mentorship, onboarding, progress tracking, safeguarding, support, accessibility, and platform improvement. This form records the user’s consent, or where required, the consent of a parent, guardian, school, NGO, or authorized partner representative.
This form should be read together with the giiconnect Privacy Policy, Cookie Policy, Terms and Conditions, AI Tutor Usage & Limitations, Child Protection and Safeguarding Addendum, Mentor Guidelines, Mentee / Learner Guidelines, and any applicable partner agreement.
2. Who Should Complete this Form
☐  Learner / Mentee aged 18 or older
☐  Parent or guardian consenting for a learner under 18
☐  Mentor or volunteer
☐  Teacher, facilitator, or hub coordinator
☐  Authorized representative of a school, NGO, community hub, government program, or partner organization
3. Participant / Account Information

4. Data giiconnect May Collect
By consenting, I understand that giiconnect may collect and process the following categories of information, depending on my role and use of the platform:
Account and identity information, such as name, email, phone number, username, password credentials, country, region, preferred language, and account role.
Learner profile information, such as education level, skill level, learning goals, course interests, language preferences, accessibility preferences, and mentorship needs.
Learning activity information, such as course enrollment, progress, quiz results, completion records, certificates, attendance, learning pathways, and dashboard activity.
Mentorship information, such as readiness questionnaires, mentor requests, mentor responses, session notes, feedback, and communication records within approved giiconnect channels.
AI Tutor information, such as prompts, questions, responses, feedback, safety flags, AI session logs, and limited interaction data used for quality, safety, and learning support.
Technical information, such as device type, app version, browser type, IP address, approximate location from network information, login logs, crash reports, sync activity, cookie preferences, and local storage settings.
Support and safeguarding information, such as help requests, complaint reports, screenshots or attachments voluntarily provided, safeguarding concerns, and investigation records where necessary.
Partner deployment information, such as cohort assignment, hub location, facilitator records, institutional enrollment data, and aggregated reporting metrics.
5. How giiconnect May Use the Data
I understand that giiconnect may use my data for the following purposes:
To create and manage my account and provide secure access to the platform.
To deliver courses, learning pathways, mentorship tools, AI Tutor support, certificates, and progress tracking.
To personalize learning recommendations, language options, accessibility features, and low-connectivity/offline learning experiences.
To connect learners with mentors and support intentional, safe, and productive mentorship relationships.
To monitor platform safety, detect misuse, investigate reports, and protect minors or vulnerable users.
To provide customer support, technical troubleshooting, onboarding assistance, and user communications.
To evaluate program outcomes, produce aggregated impact reporting, improve platform design, and support responsible data-driven decision-making.
To comply with legal, regulatory, contractual, safeguarding, audit, and recordkeeping obligations.
6. AI Tutor and Learning Analytics Consent
giiconnect may provide an AI Tutor and AI-supported learning tools. These tools are designed to support learning and mentorship preparation, not to replace teachers, mentors, professional advisers, or human judgment.
☐  I consent to the processing of my AI Tutor interactions for educational support, safety monitoring, quality improvement, and responsible platform operation.
☐  I understand that AI-generated content may be inaccurate, incomplete, biased, outdated, or inappropriate, and I should verify important information with a trusted human source.
☐  I agree not to enter highly sensitive information such as passwords, government identification numbers, medical records, financial details, or confidential personal information into the AI Tutor.
☐  For a child or learner under 18, I consent to age-appropriate AI Tutor safeguards, moderation, and logging where necessary for safety and compliance.
7. Consent for Learning Analytics and Impact Reporting
giiconnect may use learning and program data to understand whether learners are making progress and whether partner deployments are effective. Wherever possible, reporting will use aggregated or anonymized data.
☐  I consent to giiconnect using my learning progress, completion records, assessment results, attendance, and platform activity for learning analytics and program improvement.
☐  I consent to giiconnect using aggregated or anonymized data for impact reporting, grant reporting, research, funder reports, dashboard metrics, and program evaluation.
☐  I understand that giiconnect will not sell my personal data and will seek to limit identifiable data sharing to what is necessary and authorized.
8. Consent for Offline Data Storage and Synchronization
Because giiconnect is designed for low-connectivity environments, some data may be stored temporarily on a device and synchronized later when internet access is available.
☐  I consent to limited local device storage or offline caching of lessons, progress, language settings, quiz activity, and other data needed to continue learning without continuous internet access.
☐  I understand that data may synchronize once the device reconnects to the internet and that shared devices should be used carefully to protect privacy.
☐  I understand that I should log out after using shared devices and should not share my account password with others.
9. Consent for Partner / Institutional Use
Where giiconnect is used through a school, NGO, community hub, government program, employer, funder, or other partner, limited information may be shared with that partner for authorized educational, safeguarding, reporting, and program-management purposes.
☐  I consent to my relevant enrollment, attendance, progress, completion, support, and safeguarding information being shared with the authorized partner or institution administering my program, where applicable.
☐  I understand that partner access should be limited to role-based, authorized use and governed by applicable agreements, policies, and privacy obligations.
☐  For learners under 18, I authorize the relevant institution or partner to assist with account setup, consent records, safeguarding escalation, and learning support where appropriate.
10. Optional Consent: Photos, Videos, Testimonials, and Stories
This section is optional and should be signed only if the user, parent/guardian, or authorized representative agrees. Refusing this section should not prevent access to core learning services.
☐  I consent to giiconnect using my photo, video, voice, name, testimonial, learning story, or project story for communications, reports, website content, fundraising, donor engagement, social media, presentations, and impact storytelling.
☐  I consent only to anonymized stories or photos without my full name or identifying details.
☐  I do not consent to use of my photo, video, voice, name, testimonial, or personal story for external communications.
Restrictions or notes: ____________________________________________________________________________________
____________________________________________________________________________________________________________
11. Optional Consent: Research and External Evaluation
giiconnect may participate in research, external evaluation, or academic partnerships to better understand digital learning, mentorship, AI-supported education, and last-mile access. Participation in identifiable research should require additional safeguards and, where appropriate, a separate study-specific consent form.
☐  I consent to my anonymized or aggregated data being used for research, evaluation, and learning studies related to giiconnect programs.
☐  I understand that any identifiable research participation may require a separate consent process.
☐  I do not consent to use of my data for research or external evaluation beyond what is necessary for platform operation and legal compliance.
12. Data Sharing and Service Providers
I understand that giiconnect may use trusted service providers for cloud hosting, authentication, AI processing, analytics, security, communications, translation, accessibility, and technical support. giiconnect will seek to ensure that such providers use data only for authorized purposes and apply appropriate security safeguards.
☐  I acknowledge that limited data may be processed by trusted technology service providers to operate, secure, and improve the platform.
☐  I understand that data may be transferred or processed across regions, subject to appropriate contractual, technical, and organizational safeguards.
13. My Rights and Choices
Depending on applicable law, I may have rights to access, correct, delete, restrict, object to processing, withdraw consent, request data portability, or complain to a data protection authority. I understand that some data may need to be retained for legal, safeguarding, audit, dispute-resolution, or operational reasons.
Privacy requests may be sent to privacy@giiconnect.ai. Safeguarding concerns may be sent to safeguarding@giiconnect.ai.
☐  I understand that I may withdraw optional consent at any time by contacting privacy@giiconnect.ai, but withdrawal may not affect processing that occurred before withdrawal or processing required by law, safety, or legitimate operational need.
☐  I understand that withdrawal of certain required consents may limit my ability to use some giiconnect features.
14. Consent Confirmation
By signing below, I confirm that I have read and understood this Data Collection and Use Consent Form, or that it has been explained to me in a language and manner I understand. I understand what data may be collected, how it may be used, and what choices I have.
☐  I give consent for giiconnect to collect and use my data as described in this form and related policies.
☐  I am signing as a parent or guardian for a learner under 18 and confirm that I have authority to provide this consent.
☐  I am signing as an authorized representative of a partner organization and confirm that I have authority to provide consent or facilitate consent collection for the relevant deployment, subject to applicable law.
☐  I do not consent. Notes or limitations: _________________________________________________________________

15. Data Protection Officer
Privacy: privacy@giiconnect.ai
Data Protection Officer / Privacy Lead: dpo@giiconnect.ai
Safeguarding: safeguarding@giiconnect.ai
Support: support@giiconnect.ai
Legal: legal@giiconnect.ai
Website: giiconnect.ai



    `,
  },
  {
    id: "responsibilities",
    title: "Onboarding Materials & Policy",
    content: `

This document is a draft for internal review and consultation. It outlines giiconnectâs global onboarding process, safeguards, and standards for learners, mentors, and institutional partners.
Table of Contents
1. Purpose	3
2. Scope	3
3. Onboarding Phases Overview	3
4. Roles and Responsibilities	5
5. Accessibility and Inclusion	6
6. Safeguarding and Youth Protection	6
7. Data Privacy and Storage	6
8. Orientation Materials Package	7
9. Compliance Tracking and Audit	7
10. Continuous Improvement	7
11. Contact	8
1. Purpose
The onboarding process is the first step in ensuring every user’s experience on giiconnect reflects the organization’s core values: equity, inclusion, safety, and integrity.
This policy establishes a unified onboarding framework that:
Ensures legal compliance with data and safeguarding laws across multiple jurisdictions.
Verifies authenticity of mentors and partner organizations.
Introduces users to giiconnectâs core features, policies, and responsible use standards; and
Builds a consistent first experience aligned with GII’s global mission.

2. Scope
This policy applies to all new:
Learners (Mentees) — individuals registering to access courses, AI learning tools, or mentorship.
Mentors — verified professionals volunteering to support learners.
Partners and Institutions — schools, NGOs, and community centers hosting or integrating giiconnect within their programs.

3. Onboarding Phases Overview
giiconnectâs onboarding is divided into five structured phases that balance automation with human oversight.
Phase 1: Account Creation
Users create a profile through the giiconnect mobile app or web portal.
Required steps include:
Email or verified partner login.
Country and preferred language selection.
Acceptance of Terms of Service, Privacy Policy, and AI Usage & Limitations Policy.
Compliance Note: Account creation triggers data storage protections under GDPR, CCPA, and AU Digital Transformation privacy guidelines.
Users must be at least 16 years old or have guardian consent in jurisdictions requiring it.

Phase 2: Verification
Depending on user type:
Learners
Email verification link and optional two-factor authentication.
Optional school or organization affiliation confirmation.
Mentors
Identity verification (government ID or institutional email).
Professional background check or reference verification (conducted via partner NGO or local coordinator).
Digital signature of the Mentor Code of Conduct.
Partner Organizations
Submission of institutional details (registration certificate, contact person).
Review by GII’s Partnerships Team for eligibility and compliance alignment.
Safeguarding Assurance: Verification data is securely stored and deleted once verification is complete, except where legal retention is required.

Phase 3: Orientation
After verification, users are guided through a short interactive tutorial that introduces:
giiconnect Mission and Core Values — Technology, Entrepreneurship, and Education for Impact.
AI Tutor Overview — explaining its purpose, limitations, and responsible use.
Mentorship Connection Process — readiness form, respectful communication, and expectations.
Community Guidelines — respectful behavior, intellectual property respect, and safeguarding principles.
Accessibility & Support — multilingual support, offline features, and assistance channels.
Orientation completion is required before unlocking full platform access.

Phase 4: Compliance Acknowledgment
Before proceeding to live engagement or mentorship, users must acknowledge that they:
Have read and accepted the Terms of Service, Privacy Policy, and AI Tutor Usage & Limitations.
Understand that all AI content is informational and not professional advice.
Agree to abide by giiconnectâs Conduct and Safeguarding Policies.
Consent to limited data use for performance analytics and operational improvement.
This acknowledgment is digitally recorded in the user’s compliance file for audit purposes.

Phase 5: Activation
After compliance acknowledgment:
Learners gain access to courses, community groups, and the AI Tutor.
Mentors are added to the Mentorship Directory and can begin receiving requests.
Partner institutions gain dashboard access for managing learners and tracking progress.
Activation triggers the first “Welcome Series” messages:
Day 1: Welcome email with app overview.
Day 3: Resource guide on AI Tutor best practices.
Day 7: Invitation to join the community forum or local hub.

4. Roles and Responsibilities
Role
Responsibilities During Onboarding
Onboarding Manager
Oversees onboarding flow, ensures technical readiness, and monitors drop-off rates.
Safeguarding Officer
Reviews mentor verifications and investigate red flags.
AI Compliance Officer (AICO)
Ensures users are properly informed about AI limitations and responsible use.
Regional Coordinator
Handles localization, translation, and country-specific compliance.
Partner Liaison
Coordinates onboarding of institutional partners and ensures contract compliance.


5. Accessibility and Inclusion
giiconnect onboarding is designed for equal participation across all digital literacy levels.
Accessibility features include:
Audio narration and visual icons for all key steps.
Simplified mode for low-literacy users.
Translation into major African and global languages.
Offline onboarding assistance for users in low-connectivity regions.
giiconnect strives to comply with WCAG 2.2, EN 301 549, and regional accessibility laws, while acknowledging that digital conditions vary across communities.

6. Safeguarding and Youth Protection
All onboarding processes integrate child and youth safeguarding principles:
Mandatory background verification for mentors.
Content filters and topic restrictions for minors.
Clear reporting links in all youth-facing interfaces.
Staff training on recognizing and responding to safeguarding risks.
Reports or red flags during onboarding are escalated to safeguarding@giiconnect.ai within 24 hours.

7. Data Privacy and Storage
User data collected during onboarding is stored under encryption and retained only as necessary.
giiconnect complies with GDPR, CCPA, and AU privacy directives regarding consent, retention, and deletion.
Access to personal data is restricted to authorized personnel on a need-to-know basis.
Privacy inquiries: privacy@giiconnect.ai 

8. Orientation Materials Package
Every new user receives a digital orientation kit, including:
Welcome Letter from GII’s Founders.
giiconnect User Guide & Policy (Summary Edition);
AI Tutor Overview Card (responsible use highlights);
Community Standards Card.
Mentor/Mentee Quick Reference Sheet.
Safeguarding and Reporting Info Sheet.
giiconnect Help & Support Directory.
These are available both in-app and as downloadable PDFs for offline reference.

9. Compliance Tracking and Audit
The Onboarding Team maintains a Compliance Dashboard that tracks:
Completed orientation and acknowledgments.
Mentor verification logs.
Institutional onboarding progress.
Reported issues or exceptions.
Quarterly audits ensure all onboarding records are complete, accurate, and compliant with internal and legal standards.

10. Continuous Improvement
Onboarding procedures are reviewed every six months based on:
User feedback.
Regional compliance updates.
System performance analytics; and
Lessons learned from incident reviews.
Updates are approved by the Technology & Ethics Committee and documented in the Onboarding Change Log.

11. Contact
For onboarding support or verification assistance:
📧 onboarding@giiconnect.ai 
📧 safeguarding@giiconnect.ai 
📧 support@giiconnect.ai 
    `,
  },
  {
    id: "security",
    title: "Mentee(Learner) Guidelines",
    content: `
Mentee (Learner) Guidelines
(Draft Version)
This document is a draft for internal review and consultation purposes only. It does not yet represent a finalized or legally binding policy. The content herein is subject to modification following further legal and stakeholder review.
Table of Contents
1. Introduction	3
2. The Role of a Mentee	3
3. Eligibility	3
4. Core Values and Expected Conduct	4
5. Digital Etiquette	4
6. Safeguarding and Personal Safety	4
7. Data Privacy and User Rights	5
8. Using the AI Tutor Responsibly	5
9. Reporting Issues or Concerns	6
10. Building a Positive Learning Community	6
11. Accountability	6
12. Continuous Learning and Growth	7
13. Requesting Connection to a Mentor	7
14. Acknowledgment	8

1. Introduction
The giiconnect was created to open doors to knowledge, mentorship, and opportunity — wherever you are.
Learners (or “mentees”) are at the heart of this mission.
These Mentee Guidelines outline how learners can make the most of their experience on giiconnect while maintaining respect, safety, and integrity in all interactions with mentors, peers, and the GII community.
By participating in giiconnect, you join a global network of innovators and changemakers committed to empowerment through technology, education, and collaboration.

2. The Role of a Mentee
As a mentee, you are not just a learner — you are a partner in progress.
You are encouraged to:
Take initiative in your learning journey,
Engage actively with mentors and peers,
Apply the skills and lessons to real-world challenges, and
Share your experiences to inspire others in your community.
Learning with giiconnect is a shared responsibility — your curiosity, respect, and consistency create the foundation for meaningful mentorship.

3. Eligibility
Anyone passionate about learning and growth can become a giiconnect mentee.
However:
Users under 18 years of age must have parental, guardian, or institutional consent.
All mentees must register using accurate information; and
Each user may maintain only one verified account tied to their real identity.

4. Core Values and Expected Conduct
Every giiconnect learner is expected to uphold the platform’s shared values of Respect, Integrity, and Empowerment.
a. Respect
Treat mentors, peers, and GII staff with kindness and professionalism.
Use courteous language in chats, forums, and group sessions.
Respect cultural, gender, and religious diversity.
b. Integrity
Be truthful about your background and goals.
Complete your own assignments or exercises — plagiarism and dishonesty undermine trust.
Honor confidentiality — do not share other users’ information without consent.
c. Empowerment
Engage actively in discussions, workshops, and mentorship sessions.
Offer constructive feedback to mentors and fellow learners.
Use what you learn to uplift others in your community.

5. Digital Etiquette
To maintain a safe and productive learning environment, mentees must:
Avoid sending spam, inappropriate, or offensive content.
Not use the app for self-promotion, solicitation, or unrelated business.
Communicate only through approved giiconnect channels.
Respect session schedules and mentor availability.
Use clear, positive, and inclusive language in all communications.
Your digital conduct reflects your professionalism and respect for others’ time.

6. Safeguarding and Personal Safety
Your safety is our top priority.
Never share personal details such as your home address, passwords, or financial information with anyone.
Avoid one-on-one private communications with mentors outside the giiconnect platform.
Report any uncomfortable or inappropriate interactions immediately to:
📧 safeguarding@giiconnect.ai
Follow GII’s Child Protection & Safeguarding Addendum, which applies to all users under 18.
Everyone deserves to learn in a safe, supportive space — and safeguarding is a shared responsibility.

7. Data Privacy and User Rights
Your data belongs to you.
giiconnect complies with GDPR, CCPA, and African Union Data Protection laws to ensure your personal information is protected.
You can:
Review and update your data in your profile settings.
Request data deletion or correction by contacting privacy@giiconnect.ai; and
Choose whether to enable learning analytics or AI personalization features.
All personal data is encrypted, and no data is sold or shared for advertising purposes.

8. Using the AI Tutor Responsibly
The AI Tutor is designed to guide, not replace human mentorship.
You can ask questions, practice lessons, or explore ideas — but you should always think critically about its responses.
The AI Tutor:
Provides location-specific and culturally relevant information to help connect global knowledge to local opportunities.
Is available in multiple languages spoken across Africa and globally, including English, French, Swahili, and Arabic, with more being added.
Does not offer professional, medical, or legal advice.
When in doubt, verify information with your mentor or a trusted educator.

9. Reporting Issues or Concerns
If you experience technical problems, inappropriate behavior, or safety concerns, please reach out immediately.
You can contact:
📧 support@giiconnect.ai – Technical or account help
📧 safeguarding@giiconnect.ai – Youth safety and misconduct reports
📧 info@giiconnect.ai – General inquiries
Every report is treated confidentially and handled with care by GII’s support and safeguarding teams.

10. Building a Positive Learning Community
giiconnect thrives when learners uplift one another.
You can contribute by:
Participating actively in group sessions and forums,
Sharing constructive feedback,
Encouraging peers who may be struggling, and
Helping spread giiconnectâs mission of inclusion and innovation.
Great mentees don’t just absorb knowledge — they create impact by sharing it.

11. Accountability
GII may suspend or remove any account that:
Violates these guidelines or related policies,
Engages in harassment, fraud, or inappropriate conduct, or
Misuses the giiconnect platform for non-educational or harmful purposes.
Before any permanent action, GII reviews reports carefully and allows users to respond.

12. Continuous Learning and Growth
As a giiconnect learner, you are encouraged to see learning as a journey.
Use the platform’s free tools, courses, workshops, and the AI Tutor to build foundational skills before seeking direct mentorship.
The more effort you invest in learning independently, the more impactful and advanced your mentorship experience will be.

13. Requesting Connection to a Mentor
Mentorship on giiconnect is designed to be intentional, reflective, and value-driven.
To ensure that every connection between a mentee and a mentor is meaningful, mentees must complete a guided process before submitting a connection request.
This process ensures mentors receive relevant information about each mentee’s goals, experience, and readiness — maintaining professionalism and quality across the platform.
a. Purpose of the Connection Process
The process helps mentees reflect on their learning journey, demonstrate preparation, and connect with mentors whose expertise best supports their goals.
It also ensures mentees engage first with giiconnectâs free resources, courses, and AI Tutor before requesting personalized mentorship — allowing mentors to focus on higher-level guidance.
b. The Connection Request Steps
Before connecting with a mentor, the mentee must complete a short pre-generated questionnaire within the giiconnect platform.
The responses automatically accompany the connection request and help mentors assess readiness and alignment.
Typical questions may include, but are not limited to, the following:
What specific skill, topic, or goal are you seeking mentorship in?
Have you completed any related courses or training on giiconnect, or used the AI Tutor and other free resources to prepare?
What progress or experience do you already have in this area?
What challenges are you currently facing in your learning or project journey?
What do you hope to achieve through this mentorship connection?
How much time per week can you realistically commit to learning or mentorship activities?
Why do you think this mentor is suitable for you?
What research have you done about this mentor’s background, expertise, or previous contributions on giiconnect?
All responses must be honest, clear, and complete, as they form part of the mentor’s review and acceptance decision.
c. Mentor Review and Acceptance
Once submitted, the mentee’s request is securely shared with the selected mentor.
The mentor may:
Accept the request,
Recommend another mentor whose expertise better fits the mentee’s goals, or
Decline the request with constructive feedback.
This ensures that mentorship engagements are purposeful, respectful, and mutually valuable.
d. Respectful Use of the Connection System
To maintain platform quality and mentor respect:
Submit only well-prepared, goal-aligned requests.
Avoid sending multiple or random requests.
Review mentor profiles carefully before contacting them.
Repeated misuse (such as incomplete forms or spam requests) may result in temporary suspension of the connection feature.
This system builds a high-trust mentorship culture where every connection creates impact, not noise.

14. Acknowledgment
By using the giiconnect, you confirm that you have read and understood these Mentee Guidelines, and agree to follow them in accordance with giiconnectâs:
Terms of Service
Child Protection & Safeguarding Addendum
Privacy Policy
AI Tutor Usage & Limitations Policy
    `,
  },
  {
    id: "property",
    title: "AI Governance & Compliance Addendum",
    content: `

This document is an internal draft policy designed to define oversight, operational controls, and accountability mechanisms for GII’s AI systems. It does not yet represent a finalized or legally binding framework.

Table of Contents
1. Purpose	3
2. Scope	3
3. Governance Framework	3
4. Roles and Responsibilities	4
5. AI Risk Classification	5
6. AI Risk Assessment and Audit Schedule	5
7. AI Incident Reporting and Escalation	6
8. Documentation and Transparency	6
9. Legal and Ethical Compliance	7
10. Training and Awareness	7
11. Continuous Improvement	7
12. Policy Review and Amendment	7
13. Contact and Reporting Channels	8
1. Purpose
This Addendum sets forth GII’s internal structure for AI governance, safety assurance, and compliance monitoring across all giiconnect products and future AI-driven tools.
The goal is to ensure that giiconnectâs use of artificial intelligence:
Aligns with GII’s mission of inclusion, education, and empowerment.
Respects user rights and safety; and
Reduces legal, ethical, and operational risks associated with AI unpredictability.

2. Scope
This Addendum applies to:
The AI Tutor and any future AI modules integrated within giiconnect.
AI use in learning analytics, mentor matching, recommendation systems, and language translation features.
All staff, contractors, mentors, and developers with access to AI system design, deployment, or monitoring functions.

3. Governance Framework
AI oversight is administered under a multi-layer governance model to ensure continuous accountability and risk mitigation.
a. GII AI Governance Board (AIGB)
A standing board reporting to the GII Technology & Ethics Committee.
Responsibilities include:
Reviewing AI performance, ethical risk, and user safety reports quarterly.
Approving or suspending AI deployments based on risk assessments.
Overseeing compliance with evolving U.S., EU, and African AI laws and standards.
Recommending AI-related policy revisions and public disclosures.
The Board shall include at minimum:
The Chief Strategy Officer (CSO) – Chair
The Head of Technology – Deputy Chair
One Legal/Compliance representative
One Ethics and Safeguarding representative
One External advisor or partner from an academic or nonprofit AI ethics organization

4. Roles and Responsibilities
a. AI Compliance Officer (AICO)
The AICO oversees day-to-day AI governance, risk documentation, and compliance assurance.
Duties include:
Maintaining AI risk registers and model documentation.
Coordinating internal and third-party audits.
Managing incident reporting and mitigation.
Liaising with regulators, external partners, and oversight bodies.
b. AI Engineering & Product Teams
Responsible for implementing controls and ensuring safe development practices:
Embedding fairness, transparency, and explainability into all AI features.
Conducting pre-deployment risk assessments.
Testing for bias, adversarial vulnerability, and misuse.
Maintaining version control of all deployed models.
c. Safeguarding & Ethics Office
Ensures AI systems uphold ethical standards, particularly regarding minors, vulnerable users, and sensitive topics.
Tasks include:
Reviewing flagged AI interactions for compliance with safeguarding principles.
Providing feedback to developers and trainers.
Coordinating with the AICO for incidents involving youth or protected groups.
d. Regional AI Compliance Leads
Appointed for each region (Africa, Europe, North America) to:
Track regional AI regulatory developments.
Map giiconnect compliance obligations across local frameworks.
Coordinate with national digital authorities or data protection offices when required.

5. AI Risk Classification
To prioritize oversight, GII classifies all AI systems under the following tiers:
Risk Level
Definition
Governance Requirements
High-Risk
Systems influencing learning, career outcomes, or user decision-making (e.g., AI Tutor, Mentor Matching).
Mandatory quarterly review, documented human oversight, compliance with regional AI law.
Moderate-Risk
Systems that recommend content or automate non-critical workflows.
Biannual audit and user feedback monitoring.
Low-Risk
Basic assistive or analytics tools with minimal user impact.
Annual review or post-update check.

All AI systems must maintain human-in-the-loop oversight, with an escalation path to suspend functionality if risks exceed acceptable thresholds.

6. AI Risk Assessment and Audit Schedule
GII conducts both internal and external AI audits to monitor compliance and ethical integrity.
a. Internal Audits
Frequency: Quarterly for high-risk systems; annually for others.
Scope: Accuracy, bias, data handling, user safety, transparency, and model drift.
Reporting: Findings are submitted to the AI Governance Board and logged in the AI Risk Register.
b. External Audits
Frequency: At least once every 18 months, or upon significant model update.
Conducted by: Independent third-party experts or academic institutions specializing in AI ethics and safety.
c. Corrective Actions
If noncompliance or unacceptable risk is detected, the AICO initiates a Corrective Action Plan (CAP) within 30 days, outlining:
Nature of the issue.
Immediate mitigation measures.
Long-term prevention strategies; and
Verification of resolution by an independent reviewer.

7. AI Incident Reporting and Escalation
GII defines an AI incident as any occurrence where the AI system:
Produces content that could cause harm, offense, or regulatory risk.
Exposes personal or confidential information.
Behaves unpredictably or outside of established parameters.
All incidents must be reported immediately via internal channels to the AICO or Safeguarding Office.
Critical incidents are escalated within 24 hours to the CSO and AI Governance Board.

8. Documentation and Transparency
The following records are maintained by the AI Compliance Office:
AI model version history and change logs.
Risk classification and mitigation documentation.
Testing and audit reports.
User incident summaries and outcomes.
Compliance correspondence with external regulators or partners.
These documents are stored securely for a minimum of five years and may be shared with regulators or funding partners upon request.

9. Legal and Ethical Compliance
giiconnectâs AI governance aligns with international frameworks, including:
OECD AI Principles (transparency, accountability, human-centered values);
NIST AI Risk Management Framework.
EU AI Act risk-tier approach; and
African Union AI Strategy and Digital Transformation Agenda.
However, GII does not warrant or guarantee full compliance with all emerging AI laws, acknowledging that global regulatory standards evolve continuously.

10. Training and Awareness
All staff involved in AI design or oversight receive AI Ethics & Compliance Training at least annually.
Mentors and moderators receive AI Safety Orientation focused on identifying and escalating harmful or biased outputs.
Contractors or vendors working on AI components must complete pre-engagement compliance induction.

11. Continuous Improvement
AI governance is reviewed annually to ensure alignment with:
Evolving global AI regulations.
Emerging technical standards; and
Lessons learned from internal operations, audits, and incident responses.
Improvement actions are logged and tracked by the AI Compliance Officer.

12. Policy Review and Amendment
This Addendum is reviewed every 12 months or sooner if:
Significant AI-related regulatory changes occur, or
Material system updates introduce new risk categories.
Revisions require approval by the AI Governance Board and endorsement by the GII Executive Leadership Team.

13. Contact and Reporting Channels
AI Compliance Officer (AICO): aicompliance@giiconnect.ai
Safeguarding & Ethics Office: safeguarding@giiconnect.ai
Technology & Ethics Committee: ethics@giiconnect.ai
    `,
  },
  {
    id: "liability",
    title: "Help & Support Policy $ Content",
    content: `
Help & Support Policy & Content
(Draft Version)
This document is a draft for internal review. It defines how user support, issue reporting, and assistance are delivered, documented, and escalated across the giiconnect platform.

Table of Contents
1. Purpose	3
2. Scope	3
3. Support Principles	3
4. Support Channels	4
5. Support Categories & Response Times	4
6. Ticketing & Tracking System	5
7. Escalation Protocols	5
8. Safeguarding and Crisis Handling	5
9. Data Handling During Support	6
10. Accessibility & Multilingual Support	6
11. Feedback and Quality Assurance	6
12. Internal Training and Certification	7
13. Reporting and Oversight	7
14. Continuous Improvement	7
15. Contact	8
1. Purpose
The purpose of this policy is to ensure that every user of giiconnect — whether learner, mentor, or institutional partner — receives timely, respectful, and accessible assistance when facing technical issues, content concerns, or safeguarding risks.
GII recognizes that reliable support is critical to user confidence, inclusivity, and overall platform success.

2. Scope
This policy applies to:
All giiconnect users, globally.
Support staff, onboarding agents, regional coordinators, and safeguarding officers.
All channels used to provide user support, including email, live chat, phone (where applicable), and the in-app Help Center.

3. Support Principles
GII’s support system is built on the following principles:
Accessibility: Support is available to all users regardless of region, literacy level, or connectivity strength.
Empathy: Every inquiry is handled with patience, understanding, and cultural sensitivity.
Confidentiality: Personal or sensitive data shared during support interactions is protected under GII’s Privacy Policy.
Accountability: All tickets are tracked, logged, and reviewed to ensure continuous improvement.
Transparency: Users receive clear updates and closure notices for each request.

4. Support Channels
Users can reach the giiconnect support team through the following official channels:
Channel
Purpose
Availability
In-App Help Center
FAQs, tutorials, self-service troubleshooting.
24/7 (offline-enabled)
Email Support – support@giiconnect.ai
Technical, login, and content issues.
Mon–Fri, 9AM–6PM local (EAT/UTC+3)
Safeguarding Email – safeguarding@giiconnect.ai
Reports of abuse, misconduct, or harmful content.
Prioritized 24/7 review
Live Chat (Beta)
Instant technical help and escalation.
Pilot regions only (Kenya, Ghana, Côte d’Ivoire)
Regional Coordinator Hotline
Local-language support in low-connectivity areas.
As available


5. Support Categories & Response Times
Category
Description
Initial Response Time
Resolution Target
Technical
Login, app errors, data sync, updates.
Within 48 hours
3–5 business days
Account & Verification
Profile, mentor verification, data edits.
Within 48 hours
5 business days
Safeguarding or Misconduct
Harassment, youth safety, or abuse concerns.
Within 24 hours
Escalated immediately
AI or Content Feedback
Incorrect or biased AI output, content requests.
Within 72 hours
7 business days
Accessibility Support
Screen reader, translation, or usability issues.
Within 48 hours
5 business days

Note: Timelines may vary by region due to connectivity or language localization. Urgent safeguarding matters override all timelines.

6. Ticketing & Tracking System
All user requests are logged in the giiconnect Support Dashboard, which records:
Ticket number, date, and type.
Assigned staff member.
Response and resolution timestamps.
Category tags (technical, safeguarding, accessibility, etc.).
User satisfaction feedback (optional).
Each ticket remains open until confirmed as resolved or escalated to the relevant department.

7. Escalation Protocols
If an issue cannot be resolved at the first level, it is escalated as follows:
Level
Handled By
Escalation Criteria
Level 1
Support Associate
General inquiries, troubleshooting.
Level 2
Support Supervisor
Technical or repeated issues, mentor disputes.
Level 3
Department Lead (e.g., Safeguarding Officer, Legal, or AICO)
Safety, data, or compliance-related incidents.
Level 4
Executive Oversight (CSO or CEO)
Legal exposure, media attention, or regulatory involvement.

Critical cases, especially those involving minors or data breaches, are immediately escalated to Level 3.

8. Safeguarding and Crisis Handling
If a support agent identifies or receives information suggesting:
Harm to a user,
Illegal activity, or
Involvement of minors in unsafe interactions,
they must immediately:
Flag the ticket as “Safeguarding – Priority.”
Escalate to the Safeguarding Officer within one (1) hour.
Document the incident and restrict any automated follow-up messages to the affected user.
GII follows international safeguarding standards including UNICEF Child Online Protection Guidelines and African Union Child Safety in Digital Environments Framework.

9. Data Handling During Support
All support communications are stored securely and encrypted.
Sensitive user data (IDs, screenshots, documents) are automatically purged after issue resolution unless legally required for retention.
User data is never shared externally without consent or regulatory obligation.
Support staff receive privacy and safeguarding training annually.

10. Accessibility & Multilingual Support
Support is available in English and French, with expanding Swahili, Arabic, and Portuguese coverage.
Key documents and FAQs are translated using certified translation partners, not AI-only systems.
Offline support is available via community hubs or regional coordinators where internet access is limited.

11. Feedback and Quality Assurance
After resolution, users may complete an optional Support Feedback Form evaluating:
Timeliness,
Clarity,
Professionalism, and
Usefulness of the resolution.
Feedback is aggregated quarterly to improve performance metrics, training, and workflow optimization.

12. Internal Training and Certification
All support and safeguarding staff must complete:
giiconnect Support Essentials Training (on empathy, multilingual communication, and data privacy);
Safeguarding Awareness Certification; and
AI Tutor Familiarization, ensuring staff can correctly explain AI capabilities and limitations.
Refresher courses are mandatory every 12 months.

13. Reporting and Oversight
Monthly reports summarizing:
Total tickets handled,
Average response/resolution time,
Safeguarding escalations, and
User satisfaction rates,
are submitted to the Technology & Ethics Committee and reviewed during quarterly governance meetings.

14. Continuous Improvement
The Help & Support system undergoes a semiannual review to align with:
New app features or integrations,
Accessibility and multilingual updates,
Lessons from audit reports, and
Global regulatory changes affecting digital consumer rights.

15. Contact
For general or technical assistance:
📧 support@giiconnect.ai 
For safeguarding or urgent user protection concerns:
📧 safeguarding@giiconnect.ai 
For accessibility support:
📧 accessibility@giiconnect.ai 
    `,
  },
  {
    id: "Cookie Policy",
    title: "Cookie Policy",
    content: `
Cookie Policy
(Draft Version)

This document is a draft for internal review and consultation purposes only. It does not yet represent a finalized or legally binding policy. The content herein is subject to modification following further legal and regulatory review.

Table of Contents
1. Introduction	3
2. What Are Cookies?	3
3. Why We Use Cookies	3
4. Third-Party Cookies	4
5. Cookies in Low-Connectivity and Offline Environments	4
6. Managing Your Cookie Preferences	5
7. Retention and Deletion	5
8. Legal Basis for Processing (GDPR & CCPA)	5
9. Consent Banner Language	5
10. Updates to This Policy	6
11. Contact Information	6


1. Introduction
This Cookie Policy explains how Global Impact Innovators (“GII,” “We,” “Us,” or “Our”) uses cookies and similar technologies on the giiconnect and related websites and digital services (collectively, the “Services”).
It describes what cookies are, how we use them, and how you can control your preferences.
By continuing to use giiconnect after you have been presented with our cookie notice or consent settings, you agree to the use of cookies as described in this Policy.

2. What Are Cookies?
Cookies are small text files or data fragments stored on your device when you visit a website or use an app.
They help remember preferences, enable secure login, store learning progress, and optimize performance.
In giiconnect, these technologies include:
Browser cookies
Local device storage and caching (for offline or low-data environments)
Analytics tags, SDKs, and pixels (for performance monitoring)
All are designed to improve user experience and ensure secure, inclusive learning access globally.

3. Why We Use Cookies
a. Essential Cookies
Required for giiconnect to function correctly — enabling login, account management, and secure access.
b. Functional Cookies
Remember your language, theme, and accessibility preferences, ensuring continuity even offline.
c. Performance and Analytics Cookies
Collect anonymized usage metrics to improve features, reliability, and user satisfaction.
d. AI and Learning Optimization Cookies
Store progress and personalization data locally to enhance AI tutor performance and resume learning sessions seamlessly.
e. Security and Compliance Cookies
Used for fraud detection, login verification, and safeguarding compliance, protecting users (especially minors) across global hubs.

4. Third-Party Cookies
Some functions (e.g., analytics, hosting, AI services) may rely on approved third-party providers such as Google Analytics, Microsoft Azure, or other cloud and AI infrastructure partners.
All third-party cookies are governed by their respective privacy policies and are reviewed for GDPR and CCPA compliance before activation.

5. Cookies in Low-Connectivity and Offline Environments
In areas with limited or unstable internet access, giiconnect may use encrypted local device storage to:
Cache educational materials,
Store AI conversation states, and
Preserve progress securely for later synchronization.
All cached data is automatically deleted after 30 days of inactivity or when manually cleared by the user.

6. Managing Your Cookie Preferences
You can control cookies through:
The in-app “Privacy & Cookies” settings,
Your web browser preferences, or
Your mobile device privacy settings.
You may:
Accept all cookies,
Reject non-essential cookies, or
Customize your preferences.
Note that disabling some cookies may affect platform performance or feature availability.

7. Retention and Deletion
Session cookies: Deleted after logout or inactivity.
Persistent cookies: Retained for up to 12 months unless deleted earlier.
Offline cache: Auto-deleted after 30 days or upon uninstallation.

8. Legal Basis for Processing (GDPR & CCPA)
Under GDPR, our legal bases are:
Legitimate Interest – Essential cookies (Art. 6(1)(f))
Consent – Analytics, functional, and AI cookies (Art. 6(1)(a))
Under CCPA, you can opt out of data-sharing cookies via the “Do Not Sell or Share My Personal Information” option in giiconnect.

9. Consent Banner Language
When users first access giiconnect (via web, mobile, or desktop), the following message will appear in a banner or pop-up:
We Value Your Privacy
Global Impact Innovators uses cookies and local storage to enhance your learning experience, personalize content, improve app performance, and ensure platform security.
By clicking “Accept All”, you consent to the use of all cookies. You may also choose “Customize” to manage which types of cookies we store on your device, or “Reject Non-Essential Cookies” to use only essential features.
You can change your preferences anytime in Settings → Privacy & Cookies.
📘 Learn more in our Cookie Policy and Privacy Policy.
Required Buttons:
✅ Accept All
⚙️ Customize
🚫 Reject Non-Essential Cookies
Accessibility Note:
This banner will be displayed in plain, accessible English (and localized versions where available), fully compatible with screen readers, and will not block essential educational content for users in low-bandwidth environments.

10. Updates to This Policy
We may update this Policy to reflect regulatory changes or system improvements.
Any updates will be published within the app and on our website, with the date of last revision indicated.

11. Contact Information
For questions or requests related to cookies or privacy:
📧 privacy@giiconnect.ai
📧 info@giiconnect.ai
📧 safeguarding@giiconnect.ai
🌍 giiconnect.ai
    `,
  },
    {
    id: "Intellectual Property (IP) Compliance Addendum",
    title: "Intellectual Property (IP) Compliance Addendum",
    content: `
Intellectual Property (IP) Compliance Addendum
(Draft Version)

This document is a draft for internal review and does not yet represent a finalized or legally binding policy.

Table of Contents
1. Purpose	3
2. Scope	3
3. Governance Structure	3
4. IP Registration and Asset Inventory	4
5. Third-Party Content and Licensing Review	4
6. AI Content and Data Governance	5
7. User and Partner Contributions	5
8. Copyright and Trademark Protection	6
9. Monitoring and Audit Procedures	6
b. Continuous Monitoring	6
10. Infringement Response and Enforcement	7
11. Partner and Vendor Compliance	7
12. Training and Awareness	7
13. Documentation and Retention	8
14. Continuous Improvement	8
15. Contact	8


1. Purpose
The purpose of this Addendum is to define the internal systems, accountability structure, and procedures used by Global Impact Innovators (GII) to:
Protect GII’s intellectual property (IP) assets.
Ensure that all third-party content and technologies integrated into giiconnect comply with applicable copyright, trademark, and licensing laws.
Prevent unauthorized use or infringement of others’ intellectual property; and
Provide a defensible governance framework for audits, funding, and legal review.
This Addendum supports and enforces the public-facing Copyright & IP Notices Policy.

2. Scope
This Addendum applies to all:
giiconnect content, code, and materials.
Staff, contractors, mentors, and external partners involved in creating or distributing GII-related material.
Third-party systems, datasets, APIs, or assets integrated into giiconnect.
AI-generated outputs or machine-learning configurations used within the app.

3. Governance Structure
a. IP Compliance Committee (IPCC)
GII shall maintain an IP Compliance Committee reporting to the Technology & Ethics Committee.
The IPCC oversees policy enforcement, audits, and investigations involving intellectual property.
Composition:
Legal & Compliance Lead (Chair)
Technology Lead (Deputy Chair)
Program/Content Manager
External Legal Counsel (as needed)
Regional IP Representatives (Africa, EU, U.S.)
Responsibilities:
Approve all external content integrations and partnerships.
Maintain GII’s IP registry and documentation.
Monitor risks of infringement or unauthorized reuse.
Coordinate responses to infringement claims or takedown requests.
Provide quarterly compliance updates to GII’s Executive Leadership.

4. IP Registration and Asset Inventory
The Legal & Compliance team maintains an Intellectual Property Registry, which catalogs all proprietary materials owned or licensed by GII, including:
Software codebases, APIs, and databases.
Brand assets, trademarks, and design elements.
Course materials, curricula, graphics, and publications.
AI model configurations, prompts, and learning datasets.
Patents or design registrations, where applicable.
The registry includes for each asset:
Description and ownership details.
Creation date and author(s).
Licensing or usage rights.
Renewal dates or registration status.
Associated contracts or third-party permissions.
The registry is reviewed semi-annually to ensure accuracy and completeness.

5. Third-Party Content and Licensing Review
Before incorporating any external material into giiconnect, the IPCC ensures:
Licensing Verification – confirm that the content, dataset, or software is either open-source, under valid license, or approved for nonprofit educational use.
Attribution Requirements – confirm that license terms (e.g., Creative Commons, GNU, MIT) are properly followed.
Restrictions – identify any noncommercial or geographic limitations that affect redistribution.
Recordkeeping – retain proof of license or written permission.
No external materials are deployed in giiconnect production systems without documented review and sign-off by the Legal & Compliance team.

6. AI Content and Data Governance
AI-generated or AI-assisted content poses unique IP risks.
To mitigate these:
All training data and model configurations used by GII must be lawfully sourced and documented.
AI outputs are reviewed for plagiarism, copyrighted references, or proprietary data leakage before publication.
GII disclaims ownership of any AI-generated material that contains third-party protected content, unless independently verified as original.
AI model updates undergo a Data Provenance Review to confirm compliance with IP and privacy standards.
AI Governance and IP Compliance teams coordinate on every major AI system update to ensure legal traceability.

7. User and Partner Contributions
When users, mentors, or partners submit original materials (content, curricula, translations, images, etc.):
The contributor retains ownership but grants GII a non-exclusive, royalty-free license for educational and nonprofit use (as stated in the public policy).
Contributors are required to affirm that their submission is original and does not infringe on third-party rights.
The Legal team may perform spot checks or plagiarism scans on uploaded content to verify originality.
If infringement is detected, the content will be removed, and the user notified.
For formal collaborations (e.g., with schools, NGOs, or corporations), IP ownership and rights of use must be clearly defined in written agreements approved by Legal.

8. Copyright and Trademark Protection
a. Copyright Registration
Where applicable, key materials (logo designs, core training content, and AI model documentation) shall be filed for copyright protection with:
The U.S. Copyright Office, and/or
Relevant regional IP bodies (e.g., ARIPO, OAPI, or EUIPO).
b. Trademark Protection
The Legal team maintains trademark filings and renewals for:
“Global Impact Innovators”
“GII Hub”
“giiconnect”
Associated logos, emblems, and taglines.
Unauthorized or deceptive use of these marks will trigger enforcement under applicable IP law.

9. Monitoring and Audit Procedures
a. Regular IP Audits
The IPCC conducts internal audits at least once per year to review:
Proper licenses use and attribution.
Source code dependencies for unlicensed or restricted components.
Compliance of partner content and media usage.
Updates to trademark and copyright registrations.
b. Continuous Monitoring
The Legal & Technology teams jointly monitor:
Unauthorized reproductions or impersonations of giiconnect content online.
Possible brand misuse on social media or third-party sites.
Data scraping or cloning attempts against giiconnectâs platform.
Detected violations are escalated through GII’s Legal Enforcement Workflow.

10. Infringement Response and Enforcement
When a potential IP infringement is identified:
The Legal Lead logs the case in the IP Incident Register.
A preliminary review determines if the issue is internal (caused by GII staff/partners) or external (third-party violation).
If confirmed, corrective steps may include:
Immediate content removal or suspension.
Formal notice to the responsible party.
Filing of DMCA or regional equivalent takedown.
Referral to external counsel for escalation.
All enforcement actions are documented with evidence of communication and resolution outcome.

11. Partner and Vendor Compliance
Vendors, contractors, or partners working with GII must:
Provide written assurance of IP compliance for all deliverables.
Certify that materials used do not infringe third-party rights.
Transfer or license any deliverables to GII in accordance with agreed terms.
Participate in IP audits upon request.
Failure to comply may result in contract termination and potential legal recourse.

12. Training and Awareness
To promote organization-wide compliance:
All GII staff and contractors complete IP Awareness Training within 60 days of onboarding.
Annual refresher courses cover copyright, trademark, AI content, and fair use.
Mentors and content creators receive targeted guidance on attribution, open licenses, and responsible sharing.

13. Documentation and Retention
All IP-related documents (licenses, contracts, registry entries, audit reports, and correspondence) are stored in the GII Legal Repository and retained for a minimum of seven (7) years or longer as required by law.

14. Continuous Improvement
GII commits to regularly reviewing and updating IP management practices based on:
Changes in international IP law.
Lessons from audits or legal proceedings; and
Recommendations from the IPCC or external legal advisors.

15. Contact
For IP compliance questions, reporting, or escalation:
📧 legal@giiconnect.ai 
📧 ipcompliance@giiconnect.ai 
    `,
  },
    {
    id: "Terms of Service",
    title: "Terms of Service",
    content: `
Terms of Service
(Draft Version)

This document is a draft for internal review and consultation purposes only. It does not yet represent a finalized or legally binding agreement. The content herein is subject to modification following further legal, regulatory, and stakeholder review.

Table of Contents
1. Introduction	3
2. Eligibility and User Accounts	3
3. Use of Services	3
4. Data Privacy and Protection	4
5. Intellectual Property	4
6. AI-Powered Content and Limitations	5
7. Community Conduct and Safety	5
8. Third-Party Services	5
9. Limitation of Liability	6
10. Termination	6
11. Children’s Privacy and Safeguarding	6
12. Updates and Modifications	7
13. Governing Law and Dispute Resolution	7
14. Contact Information	7
15. Effective Agreement	7

1. Introduction
Welcome to giiconnect — a digital learning, mentorship, and innovation platform operated by Global Impact Innovators (GII).
These Terms of Service (“Terms”) govern your access to and use of giiconnectâs applications, websites, AI tools, mentorship programs, and related services (collectively, the “Services”).
By accessing or using giiconnect, you agree to these Terms and to all referenced policies, including the Privacy Policy and Child Protection & Safeguarding Addendum.
If you do not agree, please discontinue using our Services.
giiconnectâs mission is to promote inclusive digital education, mentorship, and entrepreneurship learning—empowering individuals and communities worldwide through technology and opportunity.

2. Eligibility and User Accounts
To create or use a giiconnect account, you must:
Be at least 13 years old, or the minimum digital-consent age in your country.
Have the legal capacity to enter into a binding agreement.
Provide accurate, current, and complete registration information.
Users under 18 must have verified parental, guardian, or institutional consent in accordance with the giiconnect Child Protection & Safeguarding Addendum.
GII reserves the right to suspend or terminate any account that violates these Terms or applicable law.

3. Use of Services
You agree to use giiconnect only for lawful, ethical, and educational purposes.
You must not:
Misuse, hack, or disrupt any giiconnect systems or networks.
Impersonate other users, mentors, or GII staff.
Upload harmful, obscene, discriminatory, or misleading content.
Reverse-engineer, duplicate, or distribute giiconnectâs code, databases, or proprietary materials.
giiconnect includes both AI-driven learning tools and human mentorship features. You acknowledge that AI outputs are for educational purposes only and not professional, legal, or medical advice.

4. Data Privacy and Protection
giiconnect complies with international privacy laws, including:
EU General Data Protection Regulation (GDPR)
U.S. California Consumer Privacy Act (CCPA/CPRA)
Children’s Online Privacy Protection Act (COPPA)
Applicable African Union and national data protection laws
Your personal data is collected and processed to:
Operate, maintain, and improve our Services.
Personalize user learning and mentorship experiences.
Ensure security, compliance, and user safety.
Data may be stored or transferred between the U.S., EU, and Africa, using Standard Contractual Clauses and other approved mechanisms to ensure consistent global protection standards.
For youth users, please refer to the giiconnect Child Protection & Safeguarding Addendum for additional privacy and safety requirements.

5. Intellectual Property
All content, trademarks, software, AI models, and digital materials on giiconnect are owned by Global Impact Innovators or licensed third parties.
Users retain ownership of the original content they create or upload but grant GII a non-exclusive, royalty-free license to use such content for operational, educational, or promotional purposes within the GII ecosystem.
Unauthorized copying, modification, or resale of giiconnectâs intellectual property is strictly prohibited.

6. AI-Powered Content and Limitations
giiconnect integrates AI tutors and AI-driven learning tools to support knowledge access and mentorship.
By using these features, you agree that:
AI responses may occasionally be incomplete, biased, or outdated.
AI outputs are not a substitute for certified education or professional expertise.
GII is not liable for outcomes based on reliance on AI-generated advice or recommendations.
Users are encouraged to verify important information through qualified mentors or subject experts.

7. Community Conduct and Safety
giiconnect promotes a respectful, inclusive learning community. You agree to:
Engage respectfully with peers and mentors.
Avoid any form of harassment, hate speech, or discrimination.
Protect your account credentials and respect others’ privacy.
Report harmful behavior immediately through the in-app reporting tool or support email.
GII reserves the right to suspend or remove content or accounts that violate these standards, in its sole discretion.

8. Third-Party Services
Certain features of giiconnect rely on third-party integrations (e.g., cloud services, AI APIs, payment processors).
While GII selects partners who maintain high compliance standards, it is not responsible for their independent actions or outages.
Third-party tools are governed by their respective privacy and service terms.

9. Limitation of Liability
To the fullest extent permitted by law, GII, its directors, affiliates, and employees are not liable for:
Indirect, incidental, or consequential damages.
Loss of data, profits, goodwill, or business opportunities.
Reliance on AI-generated content or mentor communications.
GII’s total liability for any claim under these Terms shall not exceed the amount (if any) paid by the user for premium services in the preceding 12 months.

10. Termination
You may close your account at any time via your settings or by contacting support.
GII may suspend or terminate access if you:
Breach these Terms or any related policy,
Engage in illegal or harmful behavior, or
Compromise the integrity of the community or system.
Upon termination, your rights to access the Services will cease. Some obligations (e.g., intellectual property, confidentiality, limitation of liability) will continue to apply.

11. Children’s Privacy and Safeguarding
giiconnect takes the protection of minors seriously.
For all users under 18, GII applies additional safeguards detailed in the giiconnect Child Protection & Safeguarding Addendum, which forms an integral part of these Terms.
That policy covers:
Parental consent procedures,
Mentor and staff vetting,
Secure communication protocols, and
Reporting and investigation standards.
By using giiconnect, you acknowledge and agree to the safeguarding principles outlined therein.

12. Updates and Modifications
GII may amend these Terms periodically to reflect new features, regulatory changes, or operational improvements.
Updated versions will be published on our website and app, with notice provided where legally required.
Continued use of the Services constitutes acceptance of the revised Terms.

13. Governing Law and Dispute Resolution
These Terms are governed by the following jurisdictions:
United States (State of Washington) for users in the Americas,
European Union (Brussels) for users within the EU/EEA,
Local jurisdictional law for users in Africa or other regions, consistent with GII’s operational standards.
Disputes will first be resolved through good-faith negotiation. If unresolved, they shall be subject to binding arbitration in the relevant jurisdiction, unless prohibited by local law.

14. Contact Information
For questions, concerns, safeguarding issues, or legal notices:
📧 info@giiconnect.ai
📧 safeguarding@giiconnect.ai
🌍 giiconnect.ai

15. Effective Agreement
By using giiconnect, you confirm that you have read, understood, and agree to this Draft Terms of Service and all referenced policies, including:
Privacy Policy
Cookie Policy
Child Protection & Safeguarding Addendum
Mentor Guidelines
AI Tutor Usage & Limitations Policy

    `,
  },
    {
    id: "End User License Agreement",
    title: "End User License Agreement",
    content: `

End User License Agreement
(Draft Version)


This document is a draft for internal review and consultation purposes only. It does not yet represent a finalized or legally binding agreement. The content herein is subject to modification following further legal, regulatory, and stakeholder review.
Table of Contents
1. Introduction	3
2. License Grant	3
3. Restrictions	3
4. Ownership and Intellectual Property	4
5. Data Usage and Privacy	4
6. AI Services and Limitations	4
7. Updates, Modifications, and Support	5
8. Third-Party Software and Integrations	5
9. Termination	5
10. Disclaimer of Warranties	6
11. Limitation of Liability	6
12. Compliance and Export Control	6
13. Governing Law	7
14. Contact Information	7
15. Acknowledgment	7
16. Institutional and Community Use	7

1. Introduction
This End User License Agreement (“EULA”) is a legal agreement between you (“User,” “You,” or “Your”) and Global Impact Innovators (“GII,” “We,” “Us,” or “Our”) governing your use of the giiconnect — including its software, AI systems, databases, and associated digital services (collectively, the “Software”).
By downloading, installing, accessing, or using giiconnect, you agree to be bound by this EULA and the accompanying Terms of Service, Privacy Policy, and Child Protection & Safeguarding Addendum.
If you do not agree, do not install, use, or access the Software.

2. License Grant
Global Impact Innovators grants you a limited, non-exclusive, non-transferable, revocable license to download, install, and use giiconnect solely for lawful, educational, and non-commercial purposes, subject to compliance with this EULA.
This license does not grant you ownership of the Software or any intellectual property rights contained within.

3. Restrictions
You agree not to:
Copy, reproduce, modify, or create derivative works of the Software.
Decompile, reverse-engineer, or attempt to extract source code.
Rent, lease, sell, sublicense, or distribute the Software to third parties.
Circumvent security or licensing mechanisms.
Use the Software to develop or train competing AI or educational systems.
Upload or transmit viruses, malware, or harmful code.
Any unauthorized use terminates your license automatically and may result in legal action.

4. Ownership and Intellectual Property
All rights, title, and interest in the giiconnect — including its AI models, data architecture, code, design, and documentation — remain the sole property of Global Impact Innovators or its licensors.
You retain ownership of your personal data and user-generated content but grant GII a limited license to process such data for operational, educational, and improvement purposes in line with the Privacy Policy.
All intellectual property rights are protected under applicable copyright, trademark, and international IP laws.

5. Data Usage and Privacy
By using giiconnect, you consent to GII’s processing of your personal data as outlined in the Privacy Policy.
This includes data necessary for:
Authentication and user management,
Performance tracking and learning analytics,
AI-driven personalization, and
Security and compliance monitoring.
No personal data will be sold or used for targeted advertising.
Cross-border data transfers between the U.S., EU, and Africa are governed by GDPR Standard Contractual Clauses or equivalent safeguards.
For users under 18, see the giiconnect Child Protection & Safeguarding Addendum.

6. AI Services and Limitations
The giiconnect integrates AI-powered mentorship and learning tools (“AI Tutors”). You understand and agree that:
AI responses are educational and advisory, not certified professional or academic guidance.
AI outputs may contain errors or limitations.
GII does not guarantee conformity with local curricula or accreditation standards.
Users should verify any critical information independently before acting on AI guidance.
You assume full responsibility for how you apply AI-generated information within or outside the platform.

7. Updates, Modifications, and Support
GII may periodically provide updates, patches, or new versions of the giiconnect Software to improve performance, security, or compliance.
Updates may be installed automatically or manually, depending on your device settings.
Some updates may alter or discontinue features temporarily or permanently.
GII has no obligation to provide ongoing maintenance or technical support unless legally required or explicitly stated.

8. Third-Party Software and Integrations
giiconnect may include third-party components, APIs, or cloud integrations.
Your use of these features is subject to the terms and policies of those third parties.
GII disclaims responsibility for any third-party failures, misuse, or unavailability beyond its control.

9. Termination
This EULA remains effective until terminated.
You may terminate this license at any time by deleting giiconnect and all associated files.
GII may terminate your license immediately if you:
Violate this EULA or related policies,
Misuse or harm the Software, or
Compromise data integrity, security, or user safety.
Upon termination, all rights granted to you under this EULA cease, and you must uninstall all copies of the Software.

10. Disclaimer of Warranties
The giiconnect is provided “as is” and “as available”, without warranties of any kind.
GII disclaims all express or implied warranties, including:
Merchantability or fitness for a particular purpose,
Non-infringement, and
Accuracy or reliability of AI-generated content.
Use of the Software is at your own discretion and risk.
GII does not guarantee uninterrupted or error-free operation.

11. Limitation of Liability
To the fullest extent permitted by law, Global Impact Innovators, its affiliates, and licensors are not liable for:
Any indirect, incidental, or consequential damages.
Loss of data, profits, or business opportunities.
Reliance on AI-generated or mentor-provided content.
In any case, GII’s total liability shall not exceed the amount you paid (if any) for access to the Software or premium features within the prior twelve (12) months.

12. Compliance and Export Control
You agree to comply with all applicable export and trade regulations, including the U.S. Export Administration Regulations (EAR) and EU Dual-Use Regulation.
You may not use or export giiconnect in violation of local or international sanctions, embargoes, or trade laws.

13. Governing Law
This EULA is governed by:
United States law (State of Washington) for users in the Americas,
European Union law (Brussels jurisdiction) for users within the EU/EEA,
Applicable national law for users in Africa and other regions, provided it aligns with GII’s ethical and operational standards.

14. Contact Information
For licensing questions, compliance inquiries, safeguarding concerns, or legal notices:
📧 info@giiconnect.ai
📧 safeguarding@giiconnect.ai
🌍 giiconnect.ai

15. Acknowledgment
By downloading, installing, or using the giiconnect, you acknowledge that you have read, understood, and agreed to this Draft End User License Agreement and all referenced policies, including:
Terms of Service
Privacy Policy
Child Protection & Safeguarding Addendum
AI Tutor Usage & Limitations Policy

16. Institutional and Community Use
Global Impact Innovators recognizes that giiconnect may be deployed in educational institutions, NGOs, government programs, and community hubs to promote equitable digital learning access.
Under this EULA, GII grants such institutions a non-exclusive, site-based license to install and operate giiconnect on shared or public devices, provided that:
Each installation is authorized under a formal partnership, memorandum of understanding (MOU), or community deployment agreement with GII.
The institution ensures all users comply with the Terms of Service, Privacy Policy, and Child Protection & Safeguarding Addendum.
No fees are charged to participants for access unless expressly authorized in writing by GII.
Data collected during institutional use is stored and processed in compliance with applicable privacy laws and used solely for educational and impact measurement purposes.
Mentors, facilitators, or administrators overseeing institutional use must complete GII Safeguarding and Data Protection Training prior to system activation.
Any misuse, redistribution, or installation outside the approved scope constitutes a breach of this EULA and may result in immediate termination of access and legal enforcement.

    `,
  },
    {
    id: "User Guide & Policy",
    title: "User Guide & Policy",
    content: `

User Guide & Policy
(Draft Version)







This document is a draft for internal review. It is intended to guide users on how to access, navigate, and responsibly use the giiconnect. It also establishes expectations, rights, and limitations applicable to all users worldwide.

Table of Contents
1. Introduction	3
2. Getting Started	3
2.1. Creating an Account	3
2.2. Completing Your Profile	3
3. Navigating giiconnect	4
3.1. Dashboard	4
3.2. Courses and Learning Paths	4
4. AI Tutor: Responsible Use	4
5. Mentorship Connections	5
5.1. Requesting a Mentor	5
5.2. Mentor Expectations	5
6. User Conduct and Community Standards	5
7. Accessibility and Inclusivity	5
8. Data Privacy and Security	6
9. Content Ownership	6
10. Support and Assistance	7
11. Reporting and Feedback	7
12. Disclaimer and Limitation of Liability	7
13. Policy Updates	7
14. Contact	8





1. Introduction
Welcome to the giiconnect — a platform created by Global Impact Innovators (GII) to connect learners, mentors, and innovators worldwide.
giiconnect provides access to digital courses, AI-powered learning tools, mentorship opportunities, and entrepreneurship resources — all designed to bridge digital and educational divides.
This guide serves two purposes:
To help users understand how to navigate and benefit from giiconnect.
To outline important user policies and behavioral expectations.

2. Getting Started
2.1. Creating an Account
To join giiconnect:
Download the app or visit the official giiconnect web portal.
Sign up using a valid email or verified partner account (e.g., educational institution, NGO, or government program).
Verify your identity through the secure authentication link sent to your email.
All personal data collected during registration is managed in accordance with GII’s Privacy Policy and global data protection laws.
2.2. Completing Your Profile
Your profile helps giiconnect connect you to relevant mentors, courses, and opportunities.
You may include information such as:
Education or skill level.
Areas of interest.
Languages spoken.
Preferred mentorship themes.
You can edit or delete your data anytime in your Account Settings.

3. Navigating giiconnect
3.1. Dashboard
The Dashboard is your main landing page, showing:
Current courses and progress.
Recommended mentors.
Notifications or messages.
AI Tutor shortcuts and updates.
3.2. Courses and Learning Paths
Courses are structured around technology, entrepreneurship, leadership, and community innovation.
Each course includes:
Interactive modules (text, video, or AI-guided tasks).
Knowledge assessments; and
Certificates of completion (where applicable).
Certificates are issued digitally and stored securely within your account.

4. AI Tutor: Responsible Use
The AI Tutor is an automated tool that helps explain concepts, generate ideas, and provide localized learning support.
Users should understand that:
AI outputs are informational only and may contain inaccuracies.
The AI Tutor cannot replace human expertise or mentorship.
Users remain responsible for verifying all advice or data received.
Sensitive personal, financial, or medical information should never be shared in prompts.
giiconnect does not guarantee accuracy, completeness, or safety of AI-generated outputs.

5. Mentorship Connections
5.1. Requesting a Mentor
Before sending a mentorship request, users complete a short readiness form to ensure purposeful connections.
Questions may include:
Your goals for the mentorship.
Prior related learning on giiconnect.
What research you’ve done on the mentor.
Why you believe the mentor is suitable for you.
Requests are reviewed by mentors at their discretion.
5.2. Mentor Expectations
Mentors provide voluntary guidance and feedback but do not replace formal educators or licensed professionals.
They are expected to maintain professionalism, respect user privacy, and report any safety or ethical concerns to GII.

6. User Conduct and Community Standards
By using giiconnect, users agree to:
Act respectfully and truthfully in all interactions.
Avoid harassment, hate speech, or misinformation.
Not misuse AI tools for unethical or unlawful purposes.
Respect intellectual property, data privacy, and cultural diversity.
Refrain from attempting to hack, manipulate, or copy platform systems.
Violations may result in suspension or termination of access.

7. Accessibility and Inclusivity
giiconnect is designed with accessibility and inclusivity in mind.
We are committed to aligning with:
WCAG 2.2 (Web Content Accessibility Guidelines);
EU Accessibility Act (2019/882).
African Union Digital Transformation Framework.
Local disability and ICT access laws in countries of operation.
Accessibility features include:
Multilingual support.
Text-to-speech options.
High-contrast display modes.
Offline access for low-connectivity environments.

8. Data Privacy and Security
giiconnect follows strict data protection standards under:
The EU General Data Protection Regulation (GDPR),
The U.S. Privacy Act, and
Applicable African data privacy laws.
Your personal data is never sold.
Information is stored securely, and users may request access or deletion of their data.
For data inquiries:
📧 privacy@giiconnect.ai 

9. Content Ownership
All giiconnect materials — including text, images, videos, logos, and software — are owned by Global Impact Innovators (GII) unless otherwise stated.
Users retain ownership of their original submissions but grant GII a limited license to display, adapt, and share their contributions within the platform.
Unauthorized reproduction or external redistribution is strictly prohibited.

10. Support and Assistance
For help, users can access:
The in-app Help Center (FAQs, tutorials, troubleshooting).
Live chat during working hours (availability varies by region).
Email assistance via support@giiconnect.ai. 
GII aims to respond to all support inquiries within 48 business hours, subject to capacity and connectivity.

11. Reporting and Feedback
To report technical issues, inappropriate content, or misconduct:
Use the Report button available in all modules and chats, or
Email safeguarding@giiconnect.ai for urgent safety concerns.
All reports are reviewed confidentially by the Compliance Team.

12. Disclaimer and Limitation of Liability
giiconnect is provided “as is” for educational purposes.
GII does not guarantee uninterrupted service, data accuracy, or specific results from using the platform.
Users assume full responsibility for how they interpret and apply any content, guidance, or AI output.
To the fullest extent permitted by law, GII disclaims liability for any damages or losses arising from use of the app or its features.

13. Policy Updates
This guide and policy may be updated periodically.
Continued use of giiconnect after updates constitutes acceptance of the revised terms.

14. Contact
General inquiries: info@giiconnect.ai 
Data privacy: privacy@giiconnect.ai 
Safeguarding: safeguarding@giiconnect.ai 
Technical support: support@giiconnect.ai 
    `,
  },
    {
    id: "Accessibility Statement",
    title: "Accessibility Statement",
    content: `
Accessibility Statement
(Draft Version)

This document is a draft for internal review and consultation purposes only. It does not yet represent a finalized or legally binding policy. The content herein is subject to additional accessibility testing and legal validation prior to publication.                       

Table of Contents
1. Our Commitment to Accessibility	3
2. Accessibility Principles	4
3. EU and African Accessibility Alignment	5
4. Multilingual and Cultural Accessibility	5
5. Accessibility in Low-Connectivity and Rural Settings	5
6. Testing, Audits, and Continuous Improvement	6
7. Feedback and Contact	6
8. Commitment to Inclusive and Localized Innovation	7


1. Our Commitment to Accessibility
At Global Impact Innovators (GII), accessibility is not an afterthought — it is a core value.
The giiconnect is designed to make learning, mentorship, and opportunity available to all — regardless of ability, language, or geography.
We are committed to full compliance with the following international and regional accessibility frameworks:
Global Standards
Web Content Accessibility Guidelines (WCAG) 2.2, Level AA (W3C)
UN Convention on the Rights of Persons with Disabilities (UN CRPD, Articles 9 & 21)
U.S. Standards
Americans with Disabilities Act (ADA)
Section 508 of the Rehabilitation Act
European Union Standards
EU Web Accessibility Directive (Directive (EU) 2016/2102)
European Accessibility Act (Directive (EU) 2019/882)
EN 301 549 v3.2.1 (2021-03) – Accessibility requirements for ICT products and services
African Regional Standards
African Union Digital Transformation Strategy for Africa (2020–2030) — promoting inclusive, accessible digital ecosystems across the continent.
African Telecommunications Union (ATU) ICT Accessibility Guidelines (aligned with ITU-T F.790 and ITU-T H.701) — establishing continental best practices for accessible digital services.
United Nations Economic Commission for Africa (UNECA) Policy Framework for Inclusive Digital Development — emphasizing universal access for persons with disabilities.
National Accessibility Acts or ICT Policies in countries where giiconnect operates, including (but not limited to):
Kenya – ICT Accessibility Standards under the National Council for Persons with Disabilities (NCPWD).
South Africa – Persons with Disabilities Act and ICT Accessibility Policy Framework (DTPS).
Nigeria – Discrimination Against Persons with Disabilities (Prohibition) Act, 2018.
Ghana – Persons with Disability Act, 2006 (Act 715).
Cameroon and Côte d’Ivoire – National ICT inclusion policies guided by AU and ITU standards.
Through this harmonized compliance structure, giiconnect ensures legal alignment and cultural relevance across Africa, the EU, and the United States.

2. Accessibility Principles
Our accessibility design and implementation are guided by four globally recognized principles:
a. Perceivable
All users must be able to perceive information, regardless of sensory ability.
Alt text, captions, and transcripts are available for all non-text content.
Adjustable contrast, text size, and screen orientation features are integrated.
Visual information is paired with audio or haptic signals for inclusive perception.
b. Operable
Users can navigate and control giiconnect using any input method.
Full keyboard navigation and screen reader support (NVDA, JAWS, TalkBack, VoiceOver).
Logical tab sequences and focus highlights.
Voice navigation and gesture support on mobile devices.
Time-sensitive actions provide options to pause, resume, or extend.
c. Understandable
Interfaces and content are simple, intuitive, and consistent.
Plain language across all instructions and content.
Consistent layouts and button positions.
Tooltips and on-screen guidance in multiple languages.
Localized examples and visual aids adapted to African and global contexts.
d. Robust
giiconnect maintains accessibility across devices, networks, and assistive technologies.
Fully compatible with common browsers, mobile platforms, and assistive tools.
Optimized for low-bandwidth and offline environments.
Supports progressive enhancement to ensure older devices remain functional.

3. EU and African Accessibility Alignment
giiconnectâs technical and policy framework integrates the EU’s EN 301 549 standards with African regional ICT accessibility models to achieve consistency across jurisdictions.
This means:
European Union accessibility tests (WCAG 2.2 + EN 301 549) are cross-checked against ITU and ATU accessibility requirements for Africa.
National African accessibility acts, and disability laws are mapped to giiconnectâs compliance checklist for each country of operation.
All digital products launched through GII Hubs are screened for physical and digital accessibility (including ramps, screen readers, large-key keyboards, and visual displays).
This integrated model allows giiconnect to operate globally while remaining regionally accountable.

4. Multilingual and Cultural Accessibility
Language is a form of access.
To reach learners and mentors in diverse communities, giiconnect supports:
Multilingual content in English, French, Swahili, Arabic, Portuguese, Amharic, Yoruba, and other local languages.
Contextual translation verified by regional language experts.
The AI Tutor’s ability to adapt learning responses to local dialects and contexts.
Inclusive cultural representation in images, narratives, and learning examples to prevent bias and ensure relevance.

5. Accessibility in Low-Connectivity and Rural Settings
Recognizing Africa’s unique infrastructure realities, giiconnect includes special features for accessibility in low-connectivity and rural environments:
Offline access: downloadable modules and lessons.
Lightweight architecture: designed for 2G/3G networks and low-cost devices.
Solar-powered GII Hubs: providing assistive devices and accessibility training.
Voice and SMS-based access options (under development) for users without smartphones.
giiconnect defines accessibility not only as compliance — but as connection, ensuring that all users, even in remote communities, can participate fully.

6. Testing, Audits, and Continuous Improvement
Accessibility is continually monitored through:
Automated and manual audits using WAVE, axe DevTools, and Lighthouse.
Country-level accessibility compliance mapping based on local law and AU guidelines.
Testing by users with disabilities across multiple African regions.
Quarterly reviews by GII’s Technology & Ethics Committee to align with WCAG, EN 301 549, ADA, and AU ICT Accessibility directives.
Accessibility updates and improvement logs are published with every major giiconnect version release.

7. Feedback and Contact
We welcome all feedback to improve accessibility and inclusion.
If you face any accessibility barriers or wish to suggest improvements, please contact:
📧 accessibility@giiconnect.ai 
📧 support@giiconnect.ai 
For African users, accessibility concerns may also be raised via national disability authorities or AU ICT inclusion channels, as recommended by the African Telecommunications Union (ATU).
We aim to respond within 10 business days and prioritize all usability-impacting reports.

8. Commitment to Inclusive and Localized Innovation
Accessibility at giiconnect is a living promise — to make technology truly inclusive, from Seattle to Sierra Leone.
We strive to ensure that every learner — regardless of physical ability, connectivity level, or language — can access education and opportunity through the same digital door.
By adhering to U.S., EU, and African accessibility laws and frameworks, giiconnect aims to set a new global benchmark for inclusive education technology:
Accessible by Design. Inclusive by Purpose. Local in Reach. Global in Impact.
    `,
  },
    {
    id: "Privacy Policy",
    title: "Privacy Policy",
    content: `
Privacy Policy
(Draft Version)
Last Updated: May 3, 2026



This document is a draft for internal review, operational planning, and legal validation. It is intended to describe how giiconnect collects, uses, stores, shares, protects, and deletes personal data across its app, web platform, AI-enabled learning tools, mentorship features, and partner deployments. It should be reviewed by qualified legal counsel before publication or external circulation.

Table of Contents
1. Introduction
2. Scope of This Policy
3. Who We Are and How to Contact Us
4. Personal Data We Collect
5. How We Collect Personal Data
6. Why We Use Personal Data
7. AI Tutor, Learning Analytics, and Personalization
8. Legal Bases for Processing
9. Children, Youth, and Safeguarding
10. Cookies, Local Storage, and Offline Functionality
11. How We Share Personal Data
12. International Data Transfers
13. Data Retention
14. Security Safeguards
15. Your Privacy Rights
16. Regional Privacy Rights and Notices
17. Automated Decision-Making and Profiling
18. Communications and Marketing Preferences
19. Partner and Institutional Deployments
20. Data Breach Response
21. Changes to This Policy
22. Contact Information

1. Introduction
giiconnect is a digital learning, mentorship, and opportunity platform designed to help learners, mentors, and partner institutions access digital skills, entrepreneurship education, AI-supported learning, and human mentorship in low-connectivity and underserved environments.
This Privacy Policy explains how giiconnect collects, uses, stores, shares, protects, and deletes personal data when individuals use the giiconnect mobile application, web platform, AI Tutor, mentorship tools, learning dashboards, offline features, support channels, and related services.
giiconnect is designed to support access, inclusion, and learning continuity. Because many users may access the platform through community hubs, schools, NGOs, or partner organizations, this Policy also explains how data may be handled in partner-led deployments.
2. Scope of This Policy
This Policy applies to personal data processed through:
The giiconnect mobile application and web platform.
User accounts, learner profiles, mentor profiles, and institutional dashboards.
AI Tutor interactions, learning analytics, and recommendation features.
Mentorship requests, readiness questionnaires, messages, feedback, and support tickets.
Offline caching, local device storage, synchronization tools, and low-connectivity learning features.
Partner deployments involving schools, NGOs, community hubs, training centers, or government programs.
Official giiconnect support, privacy, safeguarding, and legal communication channels.
This Policy does not replace separate agreements that may apply to institutional partners, enterprise customers, funders, or implementation partners. Where a signed agreement provides stricter privacy protections, the stricter protection will apply.
3. Who We Are and How to Contact Us
giiconnect is the app and digital platform operating under the giiconnect brand and domain. It works alongside mission-aligned nonprofit and community partners, including Global Impact Innovators (GII), but giiconnect maintains its own platform governance, data protection obligations, and product policies.
For privacy-related questions, requests, or concerns, please contact:
Privacy: privacy@giiconnect.ai
Data Protection Officer / Privacy Lead: dpo@giiconnect.ai
Safeguarding and Youth Protection: safeguarding@giiconnect.ai
General Support: support@giiconnect.ai
Legal Notices: legal@giiconnect.ai
Website: giiconnect.ai
Depending on the feature, deployment model, or partnership arrangement, giiconnect may act as a data controller, joint controller, or processor. Where a school, NGO, government agency, or other partner administers access to giiconnect, that partner may also have independent privacy responsibilities.
4. Personal Data We Collect
The types of personal data collected depend on how a user interacts with giiconnect. We aim to collect only the information reasonably necessary to provide safe, inclusive, and effective learning and mentorship services.
4.1 Account and Identity Information
Name, username, email address, phone number, and account credentials.
Country, language preference, time zone, and region.
Age range or date of birth where needed for safeguarding, eligibility, or legal compliance.
Parent, guardian, institutional, or partner consent records where required.
Identity, affiliation, or verification information for mentors and institutional administrators.
4.2 Learning and Profile Information
Education level, skill level, course interests, learning goals, and preferred mentorship themes.
Course enrollment, progress, quiz results, certificates, attendance, and completion history.
Mentorship readiness forms, mentor matching preferences, and self-reported learning challenges.
Language, accessibility, and content-format preferences.
4.3 AI Tutor and Learning Analytics Data
Prompts, questions, responses, feedback, and AI interaction logs.
Learning pathways, recommended content, completion patterns, and platform usage analytics.
Safety flags, moderation signals, and quality review records where necessary to protect users or improve system performance.
4.4 Technical, Device, and Usage Data
Device type, operating system, browser type, app version, IP address, and approximate location derived from network information.
Login history, error reports, crash logs, sync activity, and performance diagnostics.
Cookie preferences, local storage settings, and offline cache activity.
Security logs used to detect fraud, misuse, unauthorized access, or safeguarding risks.
4.5 Support, Feedback, and Communications Data
Support tickets, help center requests, complaint details, and user feedback.
Emails, forms, reports, screenshots, and attachments voluntarily submitted to giiconnect.
Safeguarding reports, incident details, and internal investigation records where necessary.
4.6 Sensitive Personal Data
giiconnect does not seek to collect sensitive personal data unless it is necessary for safeguarding, accessibility, legal compliance, or a user voluntarily provides it. Sensitive data may include disability-related accessibility needs, safeguarding information, or other protected categories under applicable law.
Where sensitive data is processed, giiconnect applies additional protections, limits access, and processes such data only where there is a lawful basis or explicit consent where required.
5. How We Collect Personal Data
We collect personal data in the following ways:
Directly from users during account creation, profile completion, course participation, support requests, mentorship forms, and AI Tutor use.
From parents, guardians, schools, NGOs, community hubs, or institutional partners where they help administer access.
Automatically through app usage, cookies, local storage, device logs, and security monitoring.
From mentors, facilitators, or administrators who provide feedback, verification information, attendance updates, or safeguarding reports.
From third-party service providers that support authentication, cloud hosting, analytics, AI functionality, translation, accessibility, or security.
6. Why We Use Personal Data
giiconnect uses personal data for the following purposes:
Purpose
Explanation
Account and access management
To create accounts, verify users, authenticate logins, manage profiles, and provide secure access.
Learning delivery
To enroll users in courses, track progress, issue certificates, recommend learning resources, and support offline learning continuity.
Mentorship
To connect learners and mentors, support readiness reviews, manage communication, and improve mentorship quality.
AI-supported learning
To operate the AI Tutor, personalize explanations, improve user experience, detect unsafe outputs, and monitor quality.
Safety and safeguarding
To protect minors and vulnerable users, investigate reports, enforce community standards, and manage escalation processes.
Accessibility and localization
To provide language support, accessibility features, low-bandwidth design, and culturally relevant learning experiences.
Platform improvement
To analyze performance, fix bugs, improve features, measure outcomes, and enhance reliability.
Legal and compliance
To comply with applicable laws, respond to lawful requests, maintain records, and enforce platform policies.


7. AI Tutor, Learning Analytics, and Personalization
giiconnect includes AI-enabled features that support learning, mentorship preparation, translation, content explanation, and guided workflows. These tools are intended to help users learn more effectively, but they do not replace human mentors, educators, or professional advisers.
AI Tutor interactions may be processed for:
Generating educational responses and learning support.
Providing personalized explanations, practice tasks, or suggested learning pathways.
Improving safety, quality, and reliability of the AI experience.
Detecting harmful, biased, unsafe, or policy-violating outputs.
Supporting internal audits, compliance review, and safeguarding investigations.
giiconnect does not intentionally use personal data from AI interactions to train public AI models. Where third-party AI providers are used, giiconnect will seek contractual safeguards limiting provider access, reuse, and retention of user data.
Users should avoid entering passwords, government identification numbers, medical records, financial details, or other highly sensitive information into the AI Tutor.
8. Legal Bases for Processing
Where laws such as the GDPR or similar data protection frameworks apply, giiconnect relies on one or more of the following legal bases:
Performance of a contract or user agreement, where processing is necessary to provide the platform and related services.
Consent, where users or guardians agree to optional features such as certain analytics, cookies, communications, or youth participation.
Legitimate interests, where processing is necessary to improve services, secure the platform, prevent misuse, support learning outcomes, or operate responsible AI systems, provided those interests are not overridden by user rights.
Legal obligation, where processing is necessary to comply with applicable laws, regulatory requests, safeguarding duties, tax obligations, or recordkeeping requirements.
Vital interests or public-interest-related safety considerations, where necessary to protect a user or another person from serious harm.
Users may withdraw consent where processing depends on consent. Withdrawal of consent does not affect processing that occurred before consent was withdrawn.
9. Children, Youth, and Safeguarding
giiconnect is designed to support youth learning and mentorship in safe, structured environments. Users under 18 may be subject to additional consent, verification, moderation, and communication protections.
For users under the applicable age of digital consent in their country, giiconnect may require parent, guardian, school, NGO, or institutional consent before account activation or access to certain features.
For children under 13 where COPPA or similar laws apply, giiconnect will seek verifiable parental consent where required before collecting personal information through online services.
Youth protection measures may include:
Restricted messaging and monitored communication channels.
Mentor verification, background checks, or institutional reference checks.
Safeguarding reporting tools and escalation procedures.
Limits on AI Tutor responses for sensitive, adult, harmful, or high-risk topics.
Additional logging where necessary to investigate safety concerns or protect users.
Safeguarding concerns should be reported immediately to safeguarding@giiconnect.ai.
10. Cookies, Local Storage, and Offline Functionality
giiconnect uses cookies, local storage, caching, and similar technologies to operate the platform, remember preferences, support offline learning, secure accounts, and improve performance.
In low-connectivity environments, giiconnect may store limited content and progress data locally on a user’s device so that learning can continue when the internet is unavailable. This may include downloaded lessons, quiz attempts, language settings, and temporary AI session states.
Users can manage cookie and local storage preferences through in-app settings, browser controls, or device settings. Disabling certain technologies may affect login, offline access, synchronization, or learning progress.
11. How We Share Personal Data
giiconnect does not sell personal data. We may share personal data only where necessary and appropriate, including with:
Service providers that support hosting, authentication, analytics, security, AI processing, translation, communications, accessibility, and technical support.
Schools, NGOs, community hubs, government programs, or institutional partners where users participate through a partner-led deployment.
Mentors or facilitators, but only the information necessary for mentorship, learning support, or safeguarding.
Professional advisers, auditors, insurers, or legal counsel where necessary for governance, compliance, or dispute resolution.
Regulators, law enforcement, courts, or public authorities where required by law or necessary to protect users, platform integrity, or legal rights.
Successor entities in connection with a merger, acquisition, restructuring, financing, or transfer of platform operations, subject to appropriate safeguards.
Where data is shared with third-party providers, giiconnect seeks to apply contractual, technical, and organizational safeguards to restrict use of the data to authorized purposes.
12. International Data Transfers
Because giiconnect serves users across multiple regions and may rely on cloud and technology providers in different jurisdictions, personal data may be transferred to or processed in countries other than the user’s country of residence.
Where applicable, giiconnect uses safeguards such as data processing agreements, Standard Contractual Clauses, regional transfer mechanisms, vendor security reviews, access controls, encryption, and data minimization practices.
Users should understand that privacy laws may differ across jurisdictions, but giiconnect seeks to maintain consistent baseline protections across all deployments.
13. Data Retention
giiconnect keeps personal data only for as long as reasonably necessary for the purposes described in this Policy, unless a longer retention period is required by law, safeguarding obligations, dispute resolution, audit, or legitimate operational needs.
Data Category
Retention Approach
Account data
Retained while the account is active and for a reasonable period after closure for security, legal, or recovery purposes.
Learning records
Retained as needed to support certificates, progress continuity, reporting, and partner program evaluation.
AI interaction logs
Retained for a limited period for safety, quality, audit, and abuse-prevention purposes, unless longer retention is required for investigations.
Support tickets
Retained as needed to resolve issues, improve support, and document compliance.
Safeguarding records
Retained according to applicable safeguarding, legal, and child protection requirements.
Offline cache
Stored locally and deleted through app settings, device controls, account closure, app removal, or automatic cleanup rules where available.


14. Security Safeguards
giiconnect applies technical and organizational safeguards designed to protect personal data against unauthorized access, loss, misuse, alteration, disclosure, or destruction.
Security measures may include:
Encryption in transit and, where appropriate, encryption at rest.
Access controls, role-based permissions, and least-privilege access.
Authentication, monitoring, logging, and suspicious activity detection.
Security testing, vulnerability management, and vendor security review.
Staff, contractor, mentor, and partner training on privacy, safeguarding, and data protection.
Incident response procedures for suspected security or privacy events.
No digital service can guarantee absolute security. Users should protect their passwords, avoid sharing account credentials, and report suspicious activity to support@giiconnect.ai.
15. Your Privacy Rights
Depending on your location and applicable law, you may have rights to:
Access the personal data giiconnect holds about you.
Correct inaccurate or incomplete personal data.
Delete personal data, subject to legal, safeguarding, or operational exceptions.
Restrict or object to certain processing.
Withdraw consent where processing depends on consent.
Request a copy of your data in a portable format where required by law.
Opt out of certain analytics, marketing, or data-sharing practices where applicable.
Appeal or complain to a data protection authority if you believe your rights have been violated.
To exercise privacy rights, contact privacy@giiconnect.ai. We may need to verify your identity before fulfilling a request. For youth users, we may also require parent, guardian, or institutional verification where legally required.
16. Regional Privacy Rights and Notices
16.1 European Union, EEA, and Similar GDPR-Style Jurisdictions
Where GDPR-style laws apply, users may have rights of access, rectification, erasure, restriction, portability, objection, and withdrawal of consent. Users may also lodge a complaint with a competent supervisory authority.
giiconnect aims to provide clear notice about the identity of the controller, processing purposes, legal bases, recipients of personal data, international transfers, retention periods, and user rights.
16.2 California and Other U.S. Privacy Rights
Where California privacy law applies, users may have rights to know, access, delete, correct, and limit certain uses of personal information. giiconnect does not sell personal data and does not knowingly sell or share personal data of minors for cross-context behavioral advertising.
California users may contact privacy@giiconnect.ai to submit a privacy rights request.
16.3 Children’s Privacy in the United States
Where COPPA applies, giiconnect will seek verifiable parental consent where required before collecting personal information online from children under 13. Parents or guardians may contact privacy@giiconnect.ai to review, correct, or request deletion of a child’s information, subject to legal and safeguarding exceptions.
16.4 Africa and Country-Specific Data Protection Laws
giiconnect is designed for use across African markets and seeks to align with applicable national data protection laws, including laws governing lawful processing, data minimization, purpose limitation, security safeguards, breach reporting, and data subject rights.
Where a local partner administers a deployment, users may also have rights under that partner’s local privacy policy or the applicable national data protection authority.
17. Automated Decision-Making and Profiling
giiconnect may use automated systems to recommend courses, suggest mentors, identify support needs, flag safety risks, detect misuse, or personalize learning content.
giiconnect does not intend to use automated processing as the sole basis for decisions that produce legal or similarly significant effects on users without appropriate human review. Users may contact privacy@giiconnect.ai to ask questions about automated recommendations or request human review where applicable.
18. Communications and Marketing Preferences
giiconnect may send service-related communications, such as account notices, security alerts, course updates, policy changes, support responses, and safeguarding notifications.
Where permitted, giiconnect may also send optional newsletters, program announcements, event invitations, or learning opportunity updates. Users may unsubscribe from optional communications at any time, but essential service and safety communications may still be sent.
19. Partner and Institutional Deployments
In some cases, users access giiconnect through a school, NGO, community hub, government program, employer, funder, or other partner. These partners may help register users, provide devices, manage cohorts, verify mentors, track learning outcomes, or provide local support.
Where a partner administers a deployment, the partner may receive limited information necessary to manage the program, such as enrollment status, progress data, attendance, completion metrics, safeguarding reports, or technical support information.
Partner access is governed by role-based permissions, data sharing limits, and applicable agreements. Partners must use personal data only for authorized educational, mentorship, safeguarding, reporting, or program-management purposes.
20. Data Breach Response
If giiconnect becomes aware of a personal data breach or security incident that may affect users, it will investigate the incident, take reasonable containment steps, assess risk, document findings, and notify affected users, partners, or regulators where required by law.
Users should report suspected unauthorized access, phishing, account compromise, or data misuse to support@giiconnect.ai or privacy@giiconnect.ai.
21. Changes to This Policy
giiconnect may update this Privacy Policy to reflect new features, legal requirements, partner deployments, security practices, or operational improvements.
When changes are material, giiconnect will provide notice through the app, website, email, partner channels, or other reasonable methods. Continued use of giiconnect after an updated Policy takes effect means the user acknowledges the revised Policy.
22. Contact Information
For privacy questions, data rights requests, safeguarding concerns, or legal notices, contact:
Privacy: privacy@giiconnect.ai
Data Protection Officer / Privacy Lead: dpo@giiconnect.ai
Safeguarding: safeguarding@giiconnect.ai
Support: support@giiconnect.ai
Legal: legal@giiconnect.ai
Website: giiconnect.ai
For urgent youth safety concerns, users, parents, guardians, mentors, facilitators, and partner organizations should contact safeguarding@giiconnect.ai immediately.

    `,
  },
];

const TermsOfService = () => {
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const section = document.getElementById(id);

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleAcceptTerms = async () => {
    try {
      setLoading(true);
      setMessage("");
      setIsError(false);

      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id;

      if (!userId) {
        setMessage("User not found. Please log in again.");
        setIsError(true);
        return;
      }

      const response = await axios.post(
        "https://backend-global-1vq9.onrender.com/api/terms/accept",
        {
          userId,
        }
      );

      if (
        response.status === 200 &&
        response.data?.msg === "Terms accepted."
      ) {
        setAccepted(true);
        setMessage("✅ You have successfully accepted the Terms of Service.");
      } else if (
        response.data?.error === "Terms already accepted."
      ) {
        setAccepted(true);
        setMessage("✅ You already accepted the Terms of Service.");
      } else {
        setMessage(
          response.data?.msg ||
            "Failed to accept Terms. Please try again."
        );
        setIsError(true);
      }
    } catch (err) {
      console.error(err);

      if (
        err.response?.data?.error === "Terms already accepted."
      ) {
        setAccepted(true);
        setMessage("✅ You already accepted the Terms of Service.");
      } else {
        setMessage("Failed to accept Terms. Please try again.");
        setIsError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // return (
  //   <Box sx={{ backgroundColor: "#F6F7F9", minHeight: "100vh" }}>
  //     <Container maxWidth="xl" sx={{ py: 5 }}>
  //       {/* PAGE TITLE */}
  //       <Typography
  //         variant="h4"
  //         fontWeight="bold"
  //         textAlign="center"
  //         sx={{ mb: 5 }}
  //       >
  //         Terms & Conditions
  //       </Typography>

  //       {/* MAIN LAYOUT */}
  //       <Box
  //         sx={{
  //           display: "flex",
  //           gap: 3,
  //           alignItems: "flex-start",
  //         }}
  //       >
  //         {/* SIDEBAR */}
  //         <Paper
  //           elevation={2}
  //           sx={{
  //             width: 280,
  //             p: 2,
  //             borderRadius: 3,
  //             position: "sticky",
  //             top: 20,
  //             height: "fit-content",
  //             display: { xs: "none", md: "block" },
  //           }}
  //         >
  //           <Typography
  //             variant="h6"
  //             fontWeight="bold"
  //             sx={{ mb: 2 }}
  //           >
  //             Sections
  //           </Typography>

  //           <List>
  //             {sections.map((section) => (
  //               <ListItemButton
  //                 key={section.id}
  //                 onClick={() => scrollToSection(section.id)}
  //                 sx={{
  //                   borderRadius: 2,
  //                   mb: 1,
  //                 }}
  //               >
  //                 <ListItemText primary={section.title} />
  //               </ListItemButton>
  //             ))}
  //           </List>
  //         </Paper>

  //         {/* MAIN CONTENT */}
  //         <Paper
  //           elevation={2}
  //           sx={{
  //             flex: 1,
  //             p: 4,
  //             borderRadius: 3,
  //             maxHeight: "85vh",
  //             overflowY: "auto",
  //             scrollBehavior: "smooth",
  //           }}
  //         >
  //           {sections.map((section, index) => (
  //             <Box
  //               key={section.id}
  //               id={section.id}
  //               sx={{ mb: 6 }}
  //             >
  //               <Typography
  //                 variant="h5"
  //                 fontWeight="bold"
  //                 sx={{ mb: 2 }}
  //               >
  //                 {section.title}
  //               </Typography>

  //               <Typography
  //                 variant="body1"
  //                 color="text.secondary"
  //                 sx={{
  //                   whiteSpace: "pre-line",
  //                   lineHeight: 1.9,
  //                 }}
  //               >
  //                 {section.content}
  //               </Typography>

  //               {index !== sections.length - 1 && (
  //                 <Divider sx={{ mt: 4 }} />
  //               )}
  //             </Box>
  //           ))}

  //           {/* ACTION BUTTONS */}
  //           <Box textAlign="center" sx={{ mt: 5 }}>
  //             {accepted ? (
  //               <Typography
  //                 variant="h6"
  //                 color="success.main"
  //                 fontWeight="bold"
  //               >
  //                 {message}
  //               </Typography>
  //             ) : (
  //               <Stack
  //                 direction={{ xs: "column", sm: "row" }}
  //                 spacing={2}
  //                 justifyContent="center"
  //               >
  //                 <Button
  //                   variant="outlined"
  //                   size="large"
  //                   onClick={() => navigate("/signup")}
  //                 >
  //                   Back To Signup
  //                 </Button>

  //                 <Button
  //                   variant="contained"
  //                   size="large"
  //                   disabled={loading}
  //                   onClick={handleAcceptTerms}
  //                   sx={{
  //                     background:
  //                       "linear-gradient(180deg, #6BD0F6, #00AEEF)",
  //                     px: 5,
  //                     py: 1.5,
  //                     fontWeight: "bold",
  //                   }}
  //                 >
  //                   {loading ? (
  //                     <CircularProgress
  //                       size={24}
  //                       color="inherit"
  //                     />
  //                   ) : (
  //                     "Accept Terms"
  //                   )}
  //                 </Button>
  //               </Stack>
  //             )}

  //             {message && !accepted && (
  //               <Typography
  //                 color={
  //                   isError ? "error" : "success.main"
  //                 }
  //                 sx={{ mt: 2 }}
  //               >
  //                 {message}
  //               </Typography>
  //             )}
  //           </Box>
  //         </Paper>
  //       </Box>
  //     </Container>

  //     <Footer />
  //   </Box>
  // );
return (
  <Box
    sx={{
      background:
        "linear-gradient(180deg, #F4F7FF 0%, #F2FAF4 100%)",
      minHeight: "100vh",
    }}
  >
    <Container maxWidth="xl" sx={{ py: 5 }}>
      {/* PAGE TITLE */}
      <Typography
        variant="h4"
        fontWeight="bold"
        textAlign="center"
        sx={{
          mb: 5,
          color: "#0A2E8A",
          letterSpacing: 0.5,
        }}
      >
        Terms & Conditions
      </Typography>

      {/* MAIN LAYOUT */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        {/* SIDEBAR */}
        <Paper
          elevation={4}
          sx={{
            width: 280,
            p: 2,
            borderRadius: 4,
            position: "sticky",
            top: 20,
            height: "fit-content",
            display: { xs: "none", md: "block" },
            background:
              "linear-gradient(180deg, #0A2E8A 0%, #10682A 100%)",
            color: "#fff",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              mb: 2,
              color: "#fff",
            }}
          >
            Sections
          </Typography>

          <List>
            {sections.map((section) => (
              <ListItemButton
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  color: "#fff",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor:
                      "rgba(255,255,255,0.12)",
                    transform: "translateX(4px)",
                  },
                }}
              >
                <ListItemText
                  primary={section.title}
                  primaryTypographyProps={{
                    fontSize: "0.95rem",
                    fontWeight: 500,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* MAIN CONTENT */}
        <Paper
          elevation={4}
          sx={{
            flex: 1,
            p: 4,
            borderRadius: 4,
            maxHeight: "85vh",
            overflowY: "auto",
            scrollBehavior: "smooth",
            backgroundColor: "#fff",
            borderTop: "6px solid #0A2E8A",
            boxShadow:
              "0 10px 30px rgba(10, 46, 138, 0.08)",

            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#0A2E8A",
              borderRadius: "10px",
            },
          }}
        >
          {sections.map((section, index) => (
            <Box
              key={section.id}
              id={section.id}
              sx={{ mb: 6 }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  mb: 2,
                  color: "#0A2E8A",
                  position: "relative",
                  display: "inline-block",

                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    bottom: -6,
                    width: 65,
                    height: 4,
                    borderRadius: 10,
                    backgroundColor: "#10682A",
                  },
                }}
              >
                {section.title}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-line",
                  lineHeight: 1.9,
                  color: "#4B5563",
                  mt: 2,
                  fontSize: "1rem",
                }}
              >
                {section.content}
              </Typography>

              {index !== sections.length - 1 && (
                <Divider
                  sx={{
                    mt: 4,
                    borderColor:
                      "rgba(16, 104, 42, 0.15)",
                  }}
                />
              )}
            </Box>
          ))}

          {/* ACTION BUTTONS */}
          <Box textAlign="center" sx={{ mt: 5 }}>
            {accepted ? (
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  color: "#10682A",
                }}
              >
                {message}
              </Typography>
            ) : (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/signup")}
                  sx={{
                    borderColor: "#0A2E8A",
                    color: "#0A2E8A",
                    px: 4,
                    py: 1.4,
                    borderRadius: 3,
                    fontWeight: "bold",
                    transition: "0.3s ease",

                    "&:hover": {
                      borderColor: "#10682A",
                      backgroundColor:
                        "rgba(16, 104, 42, 0.05)",
                    },
                  }}
                >
                  Back To Signup
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  disabled={loading}
                  onClick={handleAcceptTerms}
                  sx={{
                    background:
                      "linear-gradient(135deg, #0A2E8A 0%, #10682A 100%)",
                    px: 5,
                    py: 1.5,
                    fontWeight: "bold",
                    borderRadius: 3,
                    boxShadow:
                      "0 8px 20px rgba(10, 46, 138, 0.25)",
                    transition: "all 0.3s ease",

                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #08256F 0%, #0D5221 100%)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                    />
                  ) : (
                    "Accept Terms"
                  )}
                </Button>
              </Stack>
            )}

            {message && !accepted && (
              <Typography
                sx={{
                  mt: 2,
                  color: isError
                    ? "error.main"
                    : "#10682A",
                  fontWeight: 500,
                }}
              >
                {message}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>

    <Footer />
  </Box>
);

};

export default TermsOfService;